<script setup lang="ts">
import CardHeading from "../CardHeading.vue";
// Vue
import { ref, watch, computed } from "vue";
import { Info, Ruler } from "@lucide/vue";

// Providers
import { WizardProjectType } from "@/features/BuildingProject";
import { useReferenceAreaTypeOptions } from "@/dataProviders/ReferenceAreaTypeOptions";

// Dependencies
import { v4 as uuidv4 } from "uuid";

// Components
import SelectReferenceArea from "./SelectReferenceArea.vue";
import AddReferenceArea from "./AddReferenceArea.vue";
import DisabledAreaInputs from "./DisabledAreaInputs.vue";
import ReferenceAreaInfoModal from "./ReferenceAreaInfoModal.vue";
import StepIntro from "../StepIntro/StepIntro.vue";
import { useWizardProject } from "@/features/BuildingProject/composables/useWizardProject";

const props = withDefaults(
  defineProps<{
    wasValidated: boolean;
    project: WizardProjectType;
    /**
     * Suppress this component's own StepIntro.
     *
     * For BuildingWithReferenceArea, which renders one heading above both halves. Two StepIntros stacked
     * on a single screen would read as two steps. Additive and defaulted, so the edit wizard — which still
     * has Building as a step of its own — renders exactly as before.
     */
    hideIntro?: boolean;
  }>(),
  { hideIntro: false }
);

const referenceAreaTypeOptions = useReferenceAreaTypeOptions();

const referenceAreaIsHidden = ref<boolean>(true);
const { lens } = useWizardProject(() => props.project);

/** The two shapes the backend wants: entries is the current one, values the legacy per-type sum. */
const referenceAreaEntries = lens(
  (current) => current.emissionCalculationAssumptions.referencedAreas.entries,
  (current, value) => (current.emissionCalculationAssumptions.referencedAreas.entries = value)
);
const referenceAreaValues = lens(
  (current) => current.emissionCalculationAssumptions.referencedAreas.values,
  (current, value) => (current.emissionCalculationAssumptions.referencedAreas.values = value)
);

const referencedAreas = props.project.emissionCalculationAssumptions.referencedAreas;
const referenceAreas = ref<{ id: string; key: string; value: number | null; description: string | null }[]>(
  referencedAreas.entries && referencedAreas.entries.length > 0
    ? referencedAreas.entries.map((entry) => ({
        id: uuidv4(),
        key: entry.type,
        value: entry.area,
        description: entry.description,
      }))
    : Object.entries(referencedAreas.values).map(([key, value]) => ({
        id: uuidv4(),
        key,
        value,
        description: null,
      }))
);

const toggleReferenceAreaInfoModal = () => {
  referenceAreaIsHidden.value = !referenceAreaIsHidden.value;
};

const handleAddReferenceArea = () => {
  referenceAreas.value.push({
    id: uuidv4(),
    key: "",
    value: null,
    description: null,
  });
};

const handleRemoveReferenceArea = (id: string) => {
  referenceAreas.value = referenceAreas.value?.filter((area) => area.id !== id) || [];
};

watch(
  () => referenceAreas.value,
  (newValue) => {
    // Build the entries array for the new backend format. Rows without a structure type are kept
    // (type "") so they survive step navigation and block validation instead of silently vanishing;
    // withoutIncompleteReferenceAreaEntries strips them from every API payload.
    referenceAreaEntries.value = newValue.map((entry) => ({
      type: entry.key,
      area: entry.value,
      description: entry.description,
    }));

    // Build summed values per type for the legacy format
    const summedValues: { [key: string]: number | null } = {};

    for (const entry of newValue) {
      if (!entry.key) continue;

      if (summedValues[entry.key] === undefined) {
        summedValues[entry.key] = entry.value;
      } else {
        summedValues[entry.key] = (summedValues[entry.key] ?? 0) + (entry.value ?? 0);
      }
    }

    referenceAreaValues.value = summedValues;
  },
  { deep: true }
);

const showAddButton = computed(() => {
  return referenceAreaTypeOptions.getReferenceAreaTypeOptions().length > 0;
});

const totalReferenceArea = computed(() => {
  return referenceAreas.value.reduce((acc, curr) => {
    if (!curr.value) return acc;
    const option = referenceAreaTypeOptions.getReferenceAreaTypeOptions().find((type) => type.value === curr.key);
    if (!option) return acc;
    return acc + curr.value * option.multiplier;
  }, 0);
});
</script>

<template>
  <div class="w-full">
    <StepIntro
      v-if="!hideIntro"
      class="mb-6"
      heading="Which areas is the result measured against?"
      hint="Each type counts at its own percentage: a balcony adds a quarter of its area, not all of it." />

    <div class="rounded-8 bg-slate-0 p-px-20 flex w-full flex-col gap-3 border border-slate-300">
      <div
        class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
        <div class="flex items-center gap-2">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <Ruler
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <CardHeading class="t-sh4 text-type-default">Reference areas</CardHeading>
          <button
            type="button"
            aria-label="About reference areas"
            class="focus-ring interactive-active duration-fast ease-standard p-px-2 rounded-4 transition-colors hover:bg-slate-100"
            @click="toggleReferenceAreaInfoModal">
            <Info
              class="icon-sm text-slate-500"
              :stroke-width="1.5" />
          </button>
        </div>

        <p class="t-caption text-type-muted">
          Add a reference area if parts of the building count differently, conservatories, integrated garages and similar.
        </p>
      </div>

      <!-- The stat band earns its place only once a row exists and it becomes the visible formula
           (base + additional = total). Empty, it was a lone figure repeating the area cards above. -->
      <DisabledAreaInputs
        v-if="referenceAreas.length > 0"
        :project="project"
        :total-reference-area="totalReferenceArea" />

      <div class="w-full">
        <!-- pl-4 to match SelectReferenceArea's own p-4: the row below is a bordered box, so its
             "Building structure" label starts one full padding-width right of a flush caption. -->
        <p
          v-if="referenceAreas.length > 0"
          class="t-caption text-type-muted mb-2 pl-4">
          Additional reference areas
        </p>
        <div class="flex flex-col gap-2">
          <SelectReferenceArea
            v-for="referenceArea in referenceAreas"
            :key="referenceArea.id"
            :was-validated="props.wasValidated"
            :reference-area-type-options="referenceAreaTypeOptions.getReferenceAreaTypeOptions()"
            :item="referenceArea"
            @handle-remove-reference-area="handleRemoveReferenceArea" />
          <AddReferenceArea
            v-if="showAddButton"
            @add-reference-area="handleAddReferenceArea" />
        </div>
      </div>
    </div>

    <ReferenceAreaInfoModal
      v-if="!referenceAreaIsHidden"
      @toggle-modal="toggleReferenceAreaInfoModal" />
  </div>
</template>
